import { useEffect, useState } from "react";
import api from "../lib/api";
import { PageBody, PageHeader } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import { toast } from "sonner";

const MODELS = [
    { value: "anthropic:claude-sonnet-4-5-20250929", label: "Claude Sonnet 4.5 (default)" },
    { value: "anthropic:claude-opus-4-5-20251101", label: "Claude Opus 4.5" },
    { value: "openai:gpt-5.2", label: "OpenAI GPT-5.2" },
    { value: "openai:gpt-5", label: "OpenAI GPT-5" },
    { value: "gemini:gemini-2.5-pro", label: "Gemini 2.5 Pro" },
];

export default function Settings() {
    const [defaultModel, setDefaultModel] = useState(() => localStorage.getItem("haevn-default-model") || MODELS[0].value);
    const [maxItems, setMaxItems] = useState(() => Number(localStorage.getItem("haevn-max-items") || 100));
    const [tags, setTags] = useState([]);

    useEffect(() => {
        api.get("/trends/topics").then((r) => setTags(r.data.topics)).catch(() => {});
    }, []);

    const save = () => {
        localStorage.setItem("haevn-default-model", defaultModel);
        localStorage.setItem("haevn-max-items", String(maxItems));
        toast.success("Preferences saved (local)");
    };

    return (
        <>
            <PageHeader
                overline="Operator"
                title="Settings"
                description="Default model, collection limits, and the live tag inventory."
                testid="settings-header"
            />
            <PageBody>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 max-w-4xl">
                    <section className="border border-border bg-card p-6">
                        <div className="overline mb-2">AI</div>
                        <h3 className="font-display font-semibold text-lg mb-4">Default Analysis Model</h3>
                        <Label className="text-xs">Model</Label>
                        <Select value={defaultModel} onValueChange={setDefaultModel}>
                            <SelectTrigger data-testid="default-model-select" className="rounded-sm"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {MODELS.map((m) => (<SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>))}
                            </SelectContent>
                        </Select>
                        <p className="text-[11px] text-muted-foreground mt-2">
                            Used when running new analyses. You can override per-analysis from the Analyses page.
                        </p>

                        <div className="mt-6">
                            <Label className="text-xs">Collection limit (max items per source)</Label>
                            <Input data-testid="max-items-input" type="number" value={maxItems} onChange={(e) => setMaxItems(Number(e.target.value))} className="rounded-sm" />
                        </div>

                        <Button data-testid="save-settings-btn" className="mt-5 rounded-sm" onClick={save}>Save preferences</Button>
                    </section>

                    <section className="border border-border bg-card p-6">
                        <div className="overline mb-2">Taxonomy</div>
                        <h3 className="font-display font-semibold text-lg mb-4">Live Tag Inventory</h3>
                        {tags.length === 0 ? (
                            <p className="text-sm text-muted-foreground italic">No tags yet — they grow from sources and analyses.</p>
                        ) : (
                            <div className="flex flex-wrap gap-2">
                                {tags.map((t) => (
                                    <span key={t.topic} className="px-3 py-1 border border-border text-xs font-mono rounded-sm">
                                        {t.topic} <span className="text-muted-foreground">×{t.count}</span>
                                    </span>
                                ))}
                            </div>
                        )}
                    </section>

                    <section className="border border-border bg-card p-6 lg:col-span-2">
                        <div className="overline mb-2">Prompt framework</div>
                        <h3 className="font-display font-semibold text-lg mb-3">How HAEVN reads comments</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed max-w-3xl">
                            The system prompt is tuned to be observational rather than promotional. It looks for emotional themes,
                            verbatim repeated phrases, hidden behavioral patterns, contradictions ("X but Y"), cultural observations,
                            and emerging language. Outputs include hooks, carousel ideas, founder talking points, and podcast prompts —
                            all written to feel editorial, not algorithmic.
                        </p>
                    </section>
                </div>
            </PageBody>
        </>
    );
}
